import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { hexToRgb } from './glassEngine';

const GlassContext = createContext({ sources: [], scanId: 0 });

export function useGlass() {
  return useContext(GlassContext);
}

export default function GlassProvider({ children, scanRadius = 280 }) {
  const [scanId, setScanId] = useState(0);
  const sourcesRef = useRef([]);
  const rafRef = useRef(null);
  const lastScanRef = useRef(0);

  const scan = useCallback(() => {
    const elements = document.querySelectorAll('[data-color]');
    const newSources = [];
    elements.forEach(el => {
      const color = hexToRgb(el.dataset.color);
      if (color) {
        newSources.push({ element: el, color });
      }
    });
    sourcesRef.current = newSources;
    setScanId(id => id + 1);
  }, []);

  const requestScan = useCallback(() => {
    const now = performance.now();
    if (now - lastScanRef.current < 50) return;
    lastScanRef.current = now;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(scan);
  }, [scan]);

  useEffect(() => {
    scan();
    const t1 = setTimeout(scan, 100);
    const t2 = setTimeout(scan, 400);

    window.addEventListener('scroll', requestScan, { passive: true });
    window.addEventListener('resize', requestScan);

    const observer = new MutationObserver(requestScan);
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-color']
    });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', requestScan);
      window.removeEventListener('resize', requestScan);
      observer.disconnect();
    };
  }, [scan, requestScan]);

  return (
    <GlassContext.Provider value={{ sources: sourcesRef.current, scanId, scanRadius }}>
      {children}
    </GlassContext.Provider>
  );
}
